import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Layout/Header';
import { useAuthStore } from '../store/authStore';
import {
  getDevices,
  getAdminClients,
  createAdminClient,
  addAdminClientDevices,
  addClientUser,
  impersonateClient,
  getCurrentUser,
  resetAdminClientPassword,
  disableAdminClient,
  enableAdminClient,
  AdminClient,
  getDeviceSharingClients,
  getDeviceSharingDevices,
  getDeviceSharings,
  createDeviceSharing,
  revokeDeviceSharing,
  DeviceSharingClientOption,
  DeviceSharingDeviceOption,
  DeviceSharing,
} from '../services/api';
import { Device } from '../types';
import './ClientAccounts.css';

interface NewClientDraft {
  name: string;
  email: string;
  device_ids: number[];
}

interface AddDevicesDraft {
  clientId: number;
  email: string;
  alreadyIds: Set<number>;
  selected: number[];
}

interface NewUserDraft {
  // client_id (fleet_clients.id) — NIE Traccar user id (AdminClient.id).
  clientId: number;
  name: string;
  email: string;
}

interface PasswordReveal {
  email: string;
  password: string;
}

interface NewSharingDraft {
  device_id: number | '';
  shared_client_id: number | '';
  notes: string;
}

const errorMessage = (err: any, fallback: string): string =>
  err?.response?.data?.detail ?? fallback;

const ClientAccounts: React.FC = () => {
  const navigate = useNavigate();
  const { user, startImpersonation } = useAuthStore();

  const [clients, setClients] = useState<AdminClient[]>([]);
  const [devices, setDevices] = useState<Device[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actingIds, setActingIds] = useState<Set<number>>(new Set());
  const [rowErrors, setRowErrors] = useState<Record<number, string | null>>({});

  const [newClientDraft, setNewClientDraft] = useState<NewClientDraft | null>(null);
  const [newClientSaving, setNewClientSaving] = useState(false);
  const [newClientError, setNewClientError] = useState<string | null>(null);

  const [addDevicesDraft, setAddDevicesDraft] = useState<AddDevicesDraft | null>(null);
  const [addDevicesSaving, setAddDevicesSaving] = useState(false);
  const [addDevicesError, setAddDevicesError] = useState<string | null>(null);

  const [newUserDraft, setNewUserDraft] = useState<NewUserDraft | null>(null);
  const [newUserSaving, setNewUserSaving] = useState(false);
  const [newUserError, setNewUserError] = useState<string | null>(null);

  // Hasło tymczasowe żyje TYLKO tutaj i tylko do zamknięcia okna — nigdy nie
  // trafia do `clients`, nie jest logowane, nie ma go w żadnym innym stanie.
  const [passwordReveal, setPasswordReveal] = useState<PasswordReveal | null>(null);
  const [copyFeedback, setCopyFeedback] = useState(false);

  // ── Współdzielenie urządzeń między klientami ─────────────────────────
  const [sharings, setSharings] = useState<DeviceSharing[]>([]);
  const [sharingClients, setSharingClients] = useState<DeviceSharingClientOption[]>([]);
  const [sharingDevices, setSharingDevices] = useState<DeviceSharingDeviceOption[]>([]);
  const [sharingLoading, setSharingLoading] = useState(true);
  const [sharingListError, setSharingListError] = useState<string | null>(null);
  const [sharingActingIds, setSharingActingIds] = useState<Set<number>>(new Set());
  const [sharingRowErrors, setSharingRowErrors] = useState<Record<number, string | null>>({});

  const [newSharingDraft, setNewSharingDraft] = useState<NewSharingDraft | null>(null);
  const [newSharingSaving, setNewSharingSaving] = useState(false);
  const [newSharingError, setNewSharingError] = useState<string | null>(null);

  useEffect(() => {
    if (user && !user.administrator) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [cs, ds] = await Promise.all([getAdminClients(), getDevices()]);
      setClients(cs);
      setDevices(ds);
    } catch {
      setError('Nie udało się pobrać listy klientów');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const loadSharing = useCallback(async () => {
    setSharingLoading(true);
    setSharingListError(null);
    try {
      const [ss, cs, ds] = await Promise.all([
        getDeviceSharings(),
        getDeviceSharingClients(),
        getDeviceSharingDevices(),
      ]);
      setSharings(ss);
      setSharingClients(cs);
      setSharingDevices(ds);
    } catch {
      setSharingListError('Nie udało się pobrać listy współdzielonych urządzeń');
    } finally {
      setSharingLoading(false);
    }
  }, []);

  useEffect(() => { loadSharing(); }, [loadSharing]);

  const setRowError = (clientId: number, message: string | null) => {
    setRowErrors(prev => ({ ...prev, [clientId]: message }));
  };

  const withActing = async (clientId: number, fn: () => Promise<void>) => {
    setActingIds(prev => new Set(prev).add(clientId));
    setRowError(clientId, null);
    try {
      await fn();
    } finally {
      setActingIds(prev => {
        const next = new Set(prev);
        next.delete(clientId);
        return next;
      });
    }
  };

  // ── Nowy klient ────────────────────────────────────────────────────────

  const openNewClientModal = () => {
    setNewClientDraft({ name: '', email: '', device_ids: [] });
    setNewClientError(null);
  };

  const closeNewClientModal = () => {
    if (newClientSaving) return;
    setNewClientDraft(null);
    setNewClientError(null);
  };

  const toggleNewClientDevice = (id: number, checked: boolean) => {
    setNewClientDraft(prev => {
      if (!prev) return prev;
      const ids = new Set(prev.device_ids);
      if (checked) ids.add(id); else ids.delete(id);
      return { ...prev, device_ids: Array.from(ids) };
    });
  };

  const handleCreateClient = async () => {
    if (!newClientDraft || !newClientDraft.name.trim() || !newClientDraft.email.trim() || newClientSaving) return;
    setNewClientSaving(true);
    setNewClientError(null);
    try {
      const result = await createAdminClient({
        name: newClientDraft.name.trim(),
        email: newClientDraft.email.trim(),
        device_ids: newClientDraft.device_ids,
      });
      setNewClientDraft(null);
      await load();
      setPasswordReveal({ email: result.email, password: result.temporary_password });
    } catch (err: any) {
      setNewClientError(errorMessage(err, 'Nie udało się utworzyć klienta'));
    } finally {
      setNewClientSaving(false);
    }
  };

  // ── Dodaj urządzenia ──────────────────────────────────────────────────

  const openAddDevicesModal = (client: AdminClient) => {
    setAddDevicesDraft({
      clientId: client.id,
      email: client.email,
      alreadyIds: new Set(client.devices.map(d => d.id)),
      selected: [],
    });
    setAddDevicesError(null);
  };

  const closeAddDevicesModal = () => {
    if (addDevicesSaving) return;
    setAddDevicesDraft(null);
    setAddDevicesError(null);
  };

  const toggleAddDevice = (id: number, checked: boolean) => {
    setAddDevicesDraft(prev => {
      if (!prev) return prev;
      const ids = new Set(prev.selected);
      if (checked) ids.add(id); else ids.delete(id);
      return { ...prev, selected: Array.from(ids) };
    });
  };

  const handleAddDevices = async () => {
    if (!addDevicesDraft || addDevicesDraft.selected.length === 0 || addDevicesSaving) return;
    setAddDevicesSaving(true);
    setAddDevicesError(null);
    try {
      const result = await addAdminClientDevices(addDevicesDraft.clientId, addDevicesDraft.selected);
      setClients(prev => prev.map(c => (
        c.id === addDevicesDraft.clientId
          ? { ...c, devices: [...c.devices, ...result.devices] }
          : c
      )));
      setAddDevicesDraft(null);
    } catch (err: any) {
      setAddDevicesError(errorMessage(err, 'Nie udało się dodać urządzeń'));
    } finally {
      setAddDevicesSaving(false);
    }
  };

  // ── Dodaj użytkownika (Krok B, 2026-09-20) ────────────────────────────
  // client.client_id (fleet_clients.id) musi istnieć — dla client_id===null
  // (konto sprzed backfillu M2) przycisk w ogóle się nie renderuje.

  const openNewUserModal = (client: AdminClient) => {
    if (client.client_id === null) return;
    setNewUserDraft({ clientId: client.client_id, name: '', email: '' });
    setNewUserError(null);
  };

  const closeNewUserModal = () => {
    if (newUserSaving) return;
    setNewUserDraft(null);
    setNewUserError(null);
  };

  const handleCreateClientUser = async () => {
    if (!newUserDraft || !newUserDraft.name.trim() || !newUserDraft.email.trim() || newUserSaving) return;
    setNewUserSaving(true);
    setNewUserError(null);
    try {
      const result = await addClientUser(newUserDraft.clientId, {
        name: newUserDraft.name.trim(),
        email: newUserDraft.email.trim(),
      });
      setNewUserDraft(null);
      await load();
      setPasswordReveal({ email: result.email, password: result.temporary_password });
    } catch (err: any) {
      setNewUserError(errorMessage(err, 'Nie udało się dodać użytkownika'));
    } finally {
      setNewUserSaving(false);
    }
  };

  // ── Reset hasła ───────────────────────────────────────────────────────

  const handleResetPassword = (client: AdminClient) => {
    if (!window.confirm('Stare hasło przestanie działać. Kontynuować?')) return;
    void withActing(client.id, async () => {
      try {
        const result = await resetAdminClientPassword(client.id);
        setPasswordReveal({ email: result.email, password: result.temporary_password });
      } catch (err: any) {
        setRowError(client.id, errorMessage(err, 'Nie udało się zresetować hasła'));
      }
    });
  };

  // ── Zablokuj / odblokuj ───────────────────────────────────────────────

  const handleToggleAccess = (client: AdminClient) => {
    const disabling = !client.disabled;
    const message = disabling
      ? `Zablokować dostęp klienta ${client.email}?\n\nAktywna sesja klienta (jeśli jest zalogowany) pozostanie ważna do 24h — to nie wyloguje go natychmiast.`
      : `Odblokować dostęp klienta ${client.email}?`;
    if (!window.confirm(message)) return;
    void withActing(client.id, async () => {
      try {
        const result = disabling
          ? await disableAdminClient(client.id)
          : await enableAdminClient(client.id);
        setClients(prev => prev.map(c => (
          c.id === client.id ? { ...c, disabled: result.disabled } : c
        )));
      } catch (err: any) {
        setRowError(client.id, errorMessage(err, 'Nie udało się zmienić dostępu klienta'));
      }
    });
  };

  // ── Impersonacja admina (2026-09-20) — logowanie jako klient, CZYSTO
  // PODGLĄDOWE (backend blokuje globalnie każdy zapis wykonany tokenem
  // impersonacji, niezależnie od tego, co pozwoli zrobić ten frontend).

  const handleImpersonate = (client: AdminClient) => {
    void withActing(client.id, async () => {
      try {
        const result = await impersonateClient(client.id);
        localStorage.setItem('token', result.access_token);
        const impersonatedUser = await getCurrentUser();
        startImpersonation(result.access_token, impersonatedUser);
        navigate('/dashboard');
      } catch (err: any) {
        setRowError(client.id, errorMessage(err, 'Nie udało się zalogować jako klient'));
      }
    });
  };

  // ── Współdzielenie urządzeń ───────────────────────────────────────────

  const openNewSharingModal = () => {
    setNewSharingDraft({ device_id: '', shared_client_id: '', notes: '' });
    setNewSharingError(null);
  };

  const closeNewSharingModal = () => {
    if (newSharingSaving) return;
    setNewSharingDraft(null);
    setNewSharingError(null);
  };

  const handleCreateSharing = async () => {
    if (!newSharingDraft || newSharingDraft.device_id === '' || newSharingDraft.shared_client_id === '' || newSharingSaving) return;
    setNewSharingSaving(true);
    setNewSharingError(null);
    try {
      await createDeviceSharing({
        device_id: Number(newSharingDraft.device_id),
        shared_client_id: Number(newSharingDraft.shared_client_id),
        notes: newSharingDraft.notes.trim() || undefined,
      });
      setNewSharingDraft(null);
      await loadSharing();
    } catch (err: any) {
      setNewSharingError(errorMessage(err, 'Nie udało się udostępnić urządzenia'));
    } finally {
      setNewSharingSaving(false);
    }
  };

  const setSharingRowError = (sharingId: number, message: string | null) => {
    setSharingRowErrors(prev => ({ ...prev, [sharingId]: message }));
  };

  const handleRevokeSharing = (sharing: DeviceSharing) => {
    if (!window.confirm(`Cofnąć dostęp klienta „${sharing.shared_client_name}" do urządzenia „${sharing.device_name}"?`)) return;
    setSharingActingIds(prev => new Set(prev).add(sharing.id));
    setSharingRowError(sharing.id, null);
    void (async () => {
      try {
        await revokeDeviceSharing(sharing.id);
        await loadSharing();
      } catch (err: any) {
        setSharingRowError(sharing.id, errorMessage(err, 'Nie udało się cofnąć udostępnienia'));
      } finally {
        setSharingActingIds(prev => {
          const next = new Set(prev);
          next.delete(sharing.id);
          return next;
        });
      }
    })();
  };

  // ── Okno z hasłem tymczasowym ─────────────────────────────────────────

  const closePasswordReveal = () => {
    setPasswordReveal(null);
    setCopyFeedback(false);
  };

  const handleCopyPassword = async () => {
    if (!passwordReveal) return;
    try {
      await navigator.clipboard.writeText(passwordReveal.password);
      setCopyFeedback(true);
      setTimeout(() => setCopyFeedback(false), 2000);
    } catch {
      // Clipboard API niedostępna (brak uprawnień, kontekst bez HTTPS) —
      // hasło i tak jest widoczne w oknie do ręcznego skopiowania.
    }
  };

  if (user && !user.administrator) return null;

  const availableForNewClient = devices;
  const availableForAddDevices = addDevicesDraft
    ? devices.filter(d => !addDevicesDraft.alreadyIds.has(d.id))
    : [];

  return (
    <div className="client-accounts-layout">
      <Header />
      <div className="client-accounts-body">
        <div className="client-accounts-container">
          <div className="client-accounts-title-bar">
            <h1 className="client-accounts-title">Klienci</h1>
            <button className="client-accounts-new-btn" onClick={openNewClientModal}>
              + Nowy klient
            </button>
          </div>

          {error && <div className="client-accounts-error">{error}</div>}

          {loading ? (
            <div className="client-accounts-loading">Ładowanie…</div>
          ) : (
            <div className="client-accounts-table-wrapper">
              <table className="client-accounts-table">
                <thead>
                  <tr>
                    <th>Nazwa</th>
                    <th>Email</th>
                    <th>Status</th>
                    <th>Przypisane urządzenia</th>
                    <th className="client-accounts-actions-th">Akcje</th>
                  </tr>
                </thead>
                <tbody>
                  {clients.length === 0 && (
                    <tr>
                      <td colSpan={5} className="client-accounts-empty">
                        Brak klientów
                      </td>
                    </tr>
                  )}
                  {clients.map(c => {
                    const busy = actingIds.has(c.id);
                    const rowError = rowErrors[c.id];
                    const deviceNames = c.devices.length > 0
                      ? c.devices.map(d => d.name ?? `#${d.id}`).join(', ')
                      : '—';
                    return (
                      <React.Fragment key={c.id}>
                        <tr>
                          <td>{c.name}</td>
                          <td>
                            {c.email}
                            {c.other_users_same_client.length > 0 && (
                              <div className="client-accounts-other-users-hint">
                                {c.other_users_same_client.length === 1
                                  ? `+ inne konto: ${c.other_users_same_client[0]}`
                                  : `+${c.other_users_same_client.length} inne konta: ${c.other_users_same_client.join(', ')}`}
                              </div>
                            )}
                          </td>
                          <td>
                            <span
                              className={
                                'client-accounts-status ' +
                                (c.disabled ? 'client-accounts-status--disabled' : 'client-accounts-status--active')
                              }
                            >
                              {c.disabled ? 'zablokowany' : 'aktywny'}
                            </span>
                          </td>
                          <td className="client-accounts-devices">{deviceNames}</td>
                          <td className="client-accounts-actions">
                            <button
                              className="client-accounts-btn client-accounts-btn--add"
                              disabled={busy}
                              onClick={() => openAddDevicesModal(c)}
                            >
                              Dodaj urządzenia
                            </button>
                            {c.client_id !== null && (
                              <button
                                className="client-accounts-btn client-accounts-btn--add"
                                disabled={busy}
                                onClick={() => openNewUserModal(c)}
                              >
                                Dodaj użytkownika
                              </button>
                            )}
                            <button
                              className="client-accounts-btn client-accounts-btn--reset"
                              disabled={busy}
                              onClick={() => handleResetPassword(c)}
                            >
                              Resetuj hasło
                            </button>
                            <button
                              className={
                                'client-accounts-btn ' +
                                (c.disabled ? 'client-accounts-btn--enable' : 'client-accounts-btn--disable')
                              }
                              disabled={busy}
                              onClick={() => handleToggleAccess(c)}
                            >
                              {busy ? '…' : (c.disabled ? 'Odblokuj' : 'Zablokuj')}
                            </button>
                            <button
                              className="client-accounts-btn client-accounts-btn--impersonate"
                              disabled={busy}
                              onClick={() => handleImpersonate(c)}
                              title="Podgląd danych klienta — tryb tylko do odczytu, zapis zablokowany"
                            >
                              Zaloguj jako klient
                            </button>
                          </td>
                        </tr>
                        {rowError && (
                          <tr>
                            <td colSpan={5} className="client-accounts-row-error">{rowError}</td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="client-accounts-container">
          <div className="client-accounts-title-bar">
            <h1 className="client-accounts-title">Współdzielenie urządzeń</h1>
            <button className="client-accounts-new-btn" onClick={openNewSharingModal}>
              + Udostępnij urządzenie
            </button>
          </div>

          {sharingListError && <div className="client-accounts-error">{sharingListError}</div>}

          {sharingLoading ? (
            <div className="client-accounts-loading">Ładowanie…</div>
          ) : (
            <div className="client-accounts-table-wrapper">
              <table className="client-accounts-table">
                <thead>
                  <tr>
                    <th>Urządzenie</th>
                    <th>Właściciel</th>
                    <th>Udostępnione klientowi</th>
                    <th>Status</th>
                    <th className="client-accounts-actions-th">Akcje</th>
                  </tr>
                </thead>
                <tbody>
                  {sharings.filter(s => s.active).length === 0 && (
                    <tr>
                      <td colSpan={5} className="client-accounts-empty">
                        Brak aktywnych udostępnień
                      </td>
                    </tr>
                  )}
                  {sharings.filter(s => s.active).map(s => {
                    const busy = sharingActingIds.has(s.id);
                    const rowError = sharingRowErrors[s.id];
                    return (
                      <React.Fragment key={s.id}>
                        <tr>
                          <td>{s.device_name}</td>
                          <td>{s.owner_client_name}</td>
                          <td>{s.shared_client_name}</td>
                          <td>
                            <span className="client-accounts-status client-accounts-status--active">
                              aktywne
                            </span>
                          </td>
                          <td className="client-accounts-actions">
                            <button
                              className="client-accounts-btn client-accounts-btn--disable"
                              disabled={busy}
                              onClick={() => handleRevokeSharing(s)}
                            >
                              {busy ? '…' : 'Cofnij'}
                            </button>
                          </td>
                        </tr>
                        {rowError && (
                          <tr>
                            <td colSpan={5} className="client-accounts-row-error">{rowError}</td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {newClientDraft && (
        <div className="modal-backdrop" onClick={closeNewClientModal}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h3>Nowy klient</h3>
            {newClientError && <div className="client-accounts-modal-error">{newClientError}</div>}
            <label className="field">
              <span>Imię i nazwisko</span>
              <input
                value={newClientDraft.name}
                onChange={e => setNewClientDraft({ ...newClientDraft, name: e.target.value })}
                autoFocus
                maxLength={100}
              />
            </label>
            <label className="field">
              <span>Email</span>
              <input
                type="email"
                value={newClientDraft.email}
                onChange={e => setNewClientDraft({ ...newClientDraft, email: e.target.value })}
                maxLength={200}
              />
            </label>
            <fieldset className="field">
              <legend>Urządzenia ({newClientDraft.device_ids.length})</legend>
              <div className="client-accounts-device-list">
                {availableForNewClient.length === 0 && <span className="empty">Brak urządzeń</span>}
                {availableForNewClient.map(d => (
                  <label key={d.id} className="device-checkbox">
                    <input
                      type="checkbox"
                      checked={newClientDraft.device_ids.includes(d.id)}
                      onChange={e => toggleNewClientDevice(d.id, e.target.checked)}
                    />
                    <span>{d.name}</span>
                  </label>
                ))}
              </div>
            </fieldset>
            <div className="modal-actions">
              <button onClick={closeNewClientModal} disabled={newClientSaving}>Anuluj</button>
              <button
                onClick={handleCreateClient}
                disabled={!newClientDraft.name.trim() || !newClientDraft.email.trim() || newClientSaving}
              >
                {newClientSaving ? 'Tworzenie...' : 'Utwórz'}
              </button>
            </div>
          </div>
        </div>
      )}

      {addDevicesDraft && (
        <div className="modal-backdrop" onClick={closeAddDevicesModal}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h3>Dodaj urządzenia — {addDevicesDraft.email}</h3>
            {addDevicesError && <div className="client-accounts-modal-error">{addDevicesError}</div>}
            <fieldset className="field">
              <legend>Urządzenia ({addDevicesDraft.selected.length})</legend>
              <div className="client-accounts-device-list">
                {availableForAddDevices.length === 0 && (
                  <span className="empty">Brak nieprzypisanych urządzeń</span>
                )}
                {availableForAddDevices.map(d => (
                  <label key={d.id} className="device-checkbox">
                    <input
                      type="checkbox"
                      checked={addDevicesDraft.selected.includes(d.id)}
                      onChange={e => toggleAddDevice(d.id, e.target.checked)}
                    />
                    <span>{d.name}</span>
                  </label>
                ))}
              </div>
            </fieldset>
            <div className="modal-actions">
              <button onClick={closeAddDevicesModal} disabled={addDevicesSaving}>Anuluj</button>
              <button
                onClick={handleAddDevices}
                disabled={addDevicesDraft.selected.length === 0 || addDevicesSaving}
              >
                {addDevicesSaving ? 'Dodawanie...' : 'Dodaj'}
              </button>
            </div>
          </div>
        </div>
      )}

      {newUserDraft && (
        <div className="modal-backdrop" onClick={closeNewUserModal}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h3>Dodaj użytkownika</h3>
            {newUserError && <div className="client-accounts-modal-error">{newUserError}</div>}
            <label className="field">
              <span>Imię i nazwisko</span>
              <input
                value={newUserDraft.name}
                onChange={e => setNewUserDraft({ ...newUserDraft, name: e.target.value })}
                autoFocus
                maxLength={100}
              />
            </label>
            <label className="field">
              <span>Email</span>
              <input
                type="email"
                value={newUserDraft.email}
                onChange={e => setNewUserDraft({ ...newUserDraft, email: e.target.value })}
                maxLength={200}
              />
            </label>
            <div className="modal-actions">
              <button onClick={closeNewUserModal} disabled={newUserSaving}>Anuluj</button>
              <button
                onClick={handleCreateClientUser}
                disabled={!newUserDraft.name.trim() || !newUserDraft.email.trim() || newUserSaving}
              >
                {newUserSaving ? 'Dodawanie...' : 'Dodaj'}
              </button>
            </div>
          </div>
        </div>
      )}

      {newSharingDraft && (
        <div className="modal-backdrop" onClick={closeNewSharingModal}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h3>Udostępnij urządzenie</h3>
            {newSharingError && <div className="client-accounts-modal-error">{newSharingError}</div>}
            <label className="field">
              <span>Urządzenie</span>
              <select
                value={newSharingDraft.device_id}
                onChange={e => setNewSharingDraft({ ...newSharingDraft, device_id: e.target.value ? Number(e.target.value) : '' })}
                autoFocus
              >
                <option value="">— wybierz —</option>
                {sharingDevices.map(d => (
                  <option key={d.id} value={d.id}>
                    {d.name} {d.owner_client_name ? `(właściciel: ${d.owner_client_name})` : '(brak właściciela)'}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Udostępnij klientowi</span>
              <select
                value={newSharingDraft.shared_client_id}
                onChange={e => setNewSharingDraft({ ...newSharingDraft, shared_client_id: e.target.value ? Number(e.target.value) : '' })}
              >
                <option value="">— wybierz —</option>
                {sharingClients.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Notatka (opcjonalnie)</span>
              <input
                value={newSharingDraft.notes}
                onChange={e => setNewSharingDraft({ ...newSharingDraft, notes: e.target.value })}
                maxLength={500}
                placeholder="np. akcja odśnieżania do 15.10"
              />
            </label>
            <div className="modal-actions">
              <button onClick={closeNewSharingModal} disabled={newSharingSaving}>Anuluj</button>
              <button
                onClick={handleCreateSharing}
                disabled={newSharingDraft.device_id === '' || newSharingDraft.shared_client_id === '' || newSharingSaving}
              >
                {newSharingSaving ? 'Udostępnianie...' : 'Udostępnij'}
              </button>
            </div>
          </div>
        </div>
      )}

      {passwordReveal && (
        <div className="modal-backdrop" onClick={closePasswordReveal}>
          <div
            className="modal client-accounts-password-modal"
            onClick={e => e.stopPropagation()}
            role="alertdialog"
            aria-label="Hasło tymczasowe"
          >
            <h3>Hasło tymczasowe — {passwordReveal.email}</h3>
            <div className="client-accounts-password-value">{passwordReveal.password}</div>
            <div className="client-accounts-password-warning">
              ⚠️ Przekaż to hasło klientowi bezpiecznym kanałem (telefon/Signal), nie emailem —
              nie będzie można go odzyskać ponownie z tego miejsca.
            </div>
            <div className="modal-actions">
              <button onClick={handleCopyPassword}>
                {copyFeedback ? 'Skopiowano ✓' : 'Kopiuj do schowka'}
              </button>
              <button onClick={closePasswordReveal}>Zamknij</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientAccounts;
